import * as React from 'react';
import type { ColDef, GridApi, CellContextMenuEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
	Pencil,
	Trash2,
	Copy,
	ExternalLink,
	MoreVertical,
	FileSpreadsheet,
	FileDown,
	PlusCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { SearchInput } from '@/components/ui/search-input';
import { RefreshButton } from '@/components/ui/refresh-button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel } from '@/lib/export-utils';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

const DATE_COLUMN_KEYWORDS = [
	'date',
	'expire',
	'expiry',
	'start',
	'end',
	'created',
	'updated',
	'timestamp'
] as const;
const DATE_COLUMN_WIDTH = 120;

const isDateRelatedColumn = <TData,>(column: ColDef<TData>): boolean => {
	const matchesKeyword = (value?: string | null) => {
		if (!value) {
			return false;
		}

		const normalized = value.toLowerCase();
		return DATE_COLUMN_KEYWORDS.some(keyword =>
			normalized.includes(keyword)
		);
	};

	const columnType = column.type;
	if (Array.isArray(columnType)) {
		if (columnType.some(type => matchesKeyword(type))) {
			return true;
		}
	} else if (typeof columnType === 'string') {
		if (matchesKeyword(columnType)) {
			return true;
		}
	}

	if (typeof column.field === 'string' && matchesKeyword(column.field)) {
		return true;
	}

	if (column.colId != null && matchesKeyword(String(column.colId))) {
		return true;
	}

	if (typeof column.headerName === 'string') {
		return matchesKeyword(column.headerName);
	}

	return false;
};

interface DataTableProps<TData> {
	columns: ColDef<TData>[];
	data: TData[];
	title?: string;
	description?: string;
	showCreateButton?: boolean;
	showRefreshButton?: boolean;
	onCreate?: () => void;
	onRefresh?: () => void;
	isLoading?: boolean;
	searchPlaceholder?: string;
	showSearch?: boolean;
	onRowClick?: (row: TData) => void;
	onRowDoubleClick?: (row: TData) => void;
	onDelete?: (row: TData) => void; // For context menu delete action
	onClone?: (row: TData) => void; // For context menu clone action
	onOpenInNewWindow?: (row: TData) => void; // For context menu open in new window action
	autoSelectFirst?: boolean;
	renderCustomFilters?: () => React.ReactNode;
	selectedRowId?: string | null; // New prop for external selection control
	getRowId?: (row: TData) => string; // Function to get unique ID from row data
	exportFilename?: string; // Filename for export (without extension)
}

export function DataTable<TData>({
	columns,
	data,
	title,
	description: _description,
	showCreateButton = true,
	showRefreshButton = true,
	onCreate,
	onRefresh,
	isLoading = false,
	searchPlaceholder = 'Search...',
	showSearch = true,
	onRowClick,
	onRowDoubleClick,
	onDelete,
	onClone,
	onOpenInNewWindow,
	autoSelectFirst = true,
	renderCustomFilters,
	selectedRowId,
	getRowId,
	exportFilename
}: DataTableProps<TData>) {
	const { t } = useTranslation('common');
	const gridApiRef = React.useRef<GridApi<TData> | null>(null);
	const [searchValue, setSearchValue] = React.useState('');
	const hasAutoSelectedRef = React.useRef(false);
	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const [focusedRowIndex, setFocusedRowIndex] = React.useState<number | null>(
		null
	);
	const updateFocusedIndex = React.useCallback((index: number | null) => {
		setFocusedRowIndex(index);
	}, []);

	const enhancedColumns = React.useMemo(() => {
		return columns.map((column: ColDef<TData>, index: number) => {
			const columnDef: ColDef<TData> = {
				...column
			};
			const isDateColumn = isDateRelatedColumn(columnDef);

			// Enforce consistent width for all date-related columns
			if (isDateColumn) {
				columnDef.width = DATE_COLUMN_WIDTH;
				if ('flex' in columnDef) {
					delete columnDef.flex;
				}
			}

			if (index === 0 && columnDef.flex == null && !isDateColumn) {
				columnDef.flex = 1;
			}

			return columnDef;
		});
	}, [columns]);

	// Context menu state
	const [contextMenuRow, setContextMenuRow] = React.useState<TData | null>(
		null
	);
	const [contextMenuPosition, setContextMenuPosition] = React.useState<{
		x: number;
		y: number;
	} | null>(null);

	// Handle cell context menu (right-click)
	const handleCellContextMenu = React.useCallback(
		(event: CellContextMenuEvent<TData>) => {
			const mouseEvent = event.event as MouseEvent;
			if (mouseEvent && event.data) {
				mouseEvent.preventDefault();
				setContextMenuRow(event.data);
				setContextMenuPosition({
					x: mouseEvent.clientX,
					y: mouseEvent.clientY
				});
			}
		},
		[]
	);

	// Close context menu
	const handleCloseContextMenu = React.useCallback(() => {
		setContextMenuPosition(null);
		setContextMenuRow(null);
	}, []);

	// Context menu actions
	const handleContextMenuEdit = React.useCallback(() => {
		if (contextMenuRow && onRowDoubleClick) {
			onRowDoubleClick(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onRowDoubleClick, handleCloseContextMenu]);

	const handleContextMenuDelete = React.useCallback(() => {
		if (contextMenuRow && onDelete) {
			onDelete(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onDelete, handleCloseContextMenu]);

	const handleContextMenuClone = React.useCallback(() => {
		if (contextMenuRow && onClone) {
			onClone(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onClone, handleCloseContextMenu]);

	const handleContextMenuOpenInNewWindow = React.useCallback(() => {
		if (contextMenuRow && onOpenInNewWindow) {
			onOpenInNewWindow(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onOpenInNewWindow, handleCloseContextMenu]);

	// Export handlers
	const getExportFilename = React.useCallback(() => {
		if (exportFilename) {
			return exportFilename;
		}
		// Generate filename from title or use default
		if (title) {
			return title.toLowerCase().replace(/\s+/g, '-');
		}
		return 'export';
	}, [exportFilename, title]);

	const handleExportCSV = React.useCallback(() => {
		const api = gridApiRef.current;
		if (!api) {
			return;
		}
		exportToCSV(api, columns, { filename: getExportFilename() });
	}, [columns, getExportFilename]);

	const handleExportExcel = React.useCallback(() => {
		const api = gridApiRef.current;
		if (!api) {
			return;
		}
		exportToExcel(api, columns, {
			filename: getExportFilename(),
			sheetName: title || 'Data'
		});
	}, [columns, getExportFilename, title]);

	const handleSearchChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setSearchValue(value);

			const api = gridApiRef.current;
			if (!api) {
				return;
			}

			api.setGridOption('quickFilterText', value);
		},
		[]
	);

	const handleSearchKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== 'Escape') {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			setSearchValue('');

			const api = gridApiRef.current;

			if (!api) {
				return;
			}

			api.setGridOption('quickFilterText', '');
		},
		[]
	);

	React.useEffect(() => {
		const api = gridApiRef.current;

		if (!api) {
			return;
		}

		if (isLoading && (!data || data.length === 0)) {
			// Only show loading overlay on initial load, not during filter changes
			// This prevents interrupting AG Grid row animations
			api.showLoadingOverlay();
			return;
		}

		if (!data || data.length === 0) {
			api.showNoRowsOverlay();
			return;
		}

		api.hideOverlay();

		// Auto-select the first row when data is loaded for the first time
		if (autoSelectFirst && data.length > 0 && !hasAutoSelectedRef.current) {
			// Use setTimeout to ensure the grid is fully rendered before selection
			setTimeout(() => {
				const firstNode = api.getRowNode('0');
				if (firstNode) {
					firstNode.setSelected(true);
					hasAutoSelectedRef.current = true;
					updateFocusedIndex(0);
					// Trigger onRowClick if provided and we have data
					if (onRowClick && data[0]) {
						onRowClick(data[0]);
					}
				}
			}, 0);
		}
	}, [isLoading, data, onRowClick, autoSelectFirst]);

	// Reset auto-selection flag when data is empty (e.g., on refresh/filter)
	React.useEffect(() => {
		if (!data || data.length === 0) {
			hasAutoSelectedRef.current = false;
		}
	}, [data]);

	// Focus the grid container when route changes to this list screen
	const location = useLocation();
	React.useEffect(() => {
		// Reset auto-selection when navigating to this screen
		// This ensures first row is selected when returning to the list
		hasAutoSelectedRef.current = false;

		// Use a small delay to ensure the grid is rendered
		// Only focus container on route change, not on data updates (to preserve input focus)
		const timeoutId = setTimeout(() => {
			// Only focus if no other element is currently focused (or body is focused)
			const activeElement = document.activeElement;
			const shouldFocus =
				!activeElement ||
				activeElement === document.body ||
				activeElement.tagName === 'BODY';

			if (shouldFocus) {
				containerRef.current?.focus();
			}
		}, 100);
		return () => clearTimeout(timeoutId);
	}, [location.pathname]);

	// Handle auto-selection when data changes (separate from focus logic)
	React.useEffect(() => {
		const api = gridApiRef.current;
		if (!api || !autoSelectFirst || !data || data.length === 0) {
			return;
		}

		// Auto-select first row if no selection exists
		const selectedNodes = api.getSelectedNodes();
		if (selectedNodes.length === 0 && !hasAutoSelectedRef.current) {
			const timeoutId = setTimeout(() => {
				const firstNode = api.getDisplayedRowAtIndex(0);
				if (firstNode) {
					firstNode.setSelected(true);
					hasAutoSelectedRef.current = true;
					updateFocusedIndex(0);
					if (onRowClick && firstNode.data) {
						onRowClick(firstNode.data);
					}
				}
			}, 0);
			return () => clearTimeout(timeoutId);
		}
	}, [autoSelectFirst, data, onRowClick, updateFocusedIndex]);

	// Handle external selection changes
	// Only sync selection to grid when selectedRowId actually changes
	// We track the previous value to avoid clearing selection on data array reference changes
	const previousSelectedRowIdRef = React.useRef<string | null | undefined>(
		null
	);

	React.useEffect(() => {
		const api = gridApiRef.current;
		if (!api || !getRowId || !data || data.length === 0) {
			return;
		}

		if (!selectedRowId) {
			updateFocusedIndex(null);
			return;
		}

		// Only update selection if selectedRowId actually changed (not just data array)
		// Note: We use null as initial value, so first meaningful selection will always run
		const selectionChanged =
			previousSelectedRowIdRef.current !== selectedRowId;
		if (!selectionChanged) {
			return;
		}
		previousSelectedRowIdRef.current = selectedRowId;

		// Clear all selections first
		api.deselectAll();

		// If we have a selectedRowId, find and select that row
		if (selectedRowId) {
			// Use getRowId to find the correct row node
			const rowNode = api.getRowNode(selectedRowId);
			if (rowNode) {
				rowNode.setSelected(true);
				// Ensure the selected row is visible
				api.ensureNodeVisible(rowNode);
			}
		}
	}, [selectedRowId, data, getRowId, updateFocusedIndex]);

	const handleRowClicked = React.useCallback(
		(event: { data?: TData; rowIndex: number | null }) => {
			if (onRowClick && event.data) {
				onRowClick(event.data);
			}
			if (event.rowIndex != null) {
				updateFocusedIndex(event.rowIndex);
			}
		},
		[onRowClick, updateFocusedIndex]
	);

	const moveSelection = React.useCallback(
		(direction: 1 | -1) => {
			const api = gridApiRef.current;
			if (!api) {
				return;
			}

			const rowCount = api.getDisplayedRowCount();
			if (rowCount === 0) {
				return;
			}

			let currentIndex =
				focusedRowIndex != null
					? focusedRowIndex
					: direction > 0
						? -1
						: rowCount;
			const nextIndex = Math.max(
				0,
				Math.min(rowCount - 1, currentIndex + direction)
			);

			const node = api.getDisplayedRowAtIndex(nextIndex);
			if (!node || !node.data) {
				return;
			}

			node.setSelected(true);
			api.ensureIndexVisible(nextIndex);
			updateFocusedIndex(nextIndex);

			if (onRowClick) {
				onRowClick(node.data);
			}
		},
		[focusedRowIndex, onRowClick, updateFocusedIndex]
	);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.altKey || event.ctrlKey || event.metaKey) {
				return;
			}

			const target = event.target as HTMLElement;
			if (
				target &&
				['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(
					target.tagName
				)
			) {
				return;
			}

			if (event.key === 'ArrowDown') {
				event.preventDefault();
				moveSelection(1);
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				moveSelection(-1);
			} else if (event.key === 'Enter' && onRowDoubleClick) {
				event.preventDefault();
				const api = gridApiRef.current;
				if (!api) {
					return;
				}

				// Get the currently selected row
				const selectedNodes = api.getSelectedNodes();
				if (selectedNodes.length > 0 && selectedNodes[0].data) {
					onRowDoubleClick(selectedNodes[0].data);
				}
			}
		},
		[moveSelection, onRowDoubleClick]
	);

	return (
		<div
			ref={containerRef}
			tabIndex={0}
			onKeyDown={handleKeyDown}
			className="flex flex-col w-full h-full space-y-3 outline-none focus:outline-none"
		>
			{/* Toolbar */}
			<div className="flex items-center gap-3 px-4 pt-4">
				{showSearch && (
					<SearchInput
						placeholder={searchPlaceholder}
						value={searchValue}
						onChange={handleSearchChange}
						onKeyDown={handleSearchKeyDown}
						variant="transparent"
						className="max-w-sm flex-1 min-w-0"
					/>
				)}
				{renderCustomFilters && renderCustomFilters()}
				<div className="flex items-center gap-1 ml-auto flex-shrink-0">
					{showCreateButton && onCreate && (
						<button
							type="button"
							onClick={onCreate}
							className="inline-flex items-center justify-center h-9 w-9 rounded-md text-blue-600 dark:text-blue-400 transition-colors hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:text-blue-700 dark:hover:text-blue-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							title={t('buttons.addNew')}
							aria-label={t('buttons.addNew')}
						>
							<PlusCircle className="h-5 w-5" strokeWidth={2.25} />
						</button>
					)}
					{showRefreshButton && (
						<RefreshButton
							onRefresh={onRefresh}
							isLoading={isLoading}
							showLabel={false}
							variant="ghost"
							size="icon"
							className="h-9 w-9 text-muted-foreground hover:text-foreground"
						/>
					)}
					{/* Options Menu */}
					<DropdownMenu>
						<DropdownMenuTrigger className="inline-flex items-center justify-center rounded-md h-9 w-9 text-muted-foreground transition-colors hover:text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
							<MoreVertical className="h-4 w-4" />
							<span className="sr-only">
								{t('buttons.options')}
							</span>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-48">
							<DropdownMenuItem onClick={handleExportExcel}>
								<FileSpreadsheet className="mr-2 h-4 w-4" />
								{t('export.toExcel')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={handleExportCSV}>
								<FileDown className="mr-2 h-4 w-4" />
								{t('export.toCSV')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Table */}
			<div className="flex flex-col flex-1 overflow-hidden">
				<div
					className="flex-1 overflow-hidden ag-theme-quartz vectro-ag-theme"
					onContextMenu={e => e.preventDefault()}
				>
					<AgGridReact<TData>
						theme="legacy"
						rowData={data}
						columnDefs={enhancedColumns}
						rowHeight={50}
						headerHeight={40}
						defaultColDef={{
							sortable: true,
							resizable: true
						}}
						animateRows={true}
						getRowId={
							getRowId
								? params => getRowId(params.data)
								: undefined
						}
						suppressPaginationPanel
						suppressCellFocus
						rowSelection="single"
						suppressContextMenu={true}
						onCellContextMenu={handleCellContextMenu}
						overlayLoadingTemplate="<div class='ag-custom-overlay'><div class='ag-custom-overlay__title'>Loading...</div></div>"
						overlayNoRowsTemplate="<div class='ag-custom-overlay'><div class='ag-custom-overlay__title'>No results</div><div class='ag-custom-overlay__subtitle'>Try adjusting your search or data source.</div></div>"
						onGridReady={params => {
							gridApiRef.current = params.api;

							if (isLoading) {
								params.api.showLoadingOverlay();
							}
						}}
						onRowClicked={handleRowClicked}
						onRowDoubleClicked={event => {
							if (onRowDoubleClick && event.data) {
								onRowDoubleClick(event.data);
							}
						}}
					/>
				</div>
			</div>

			{/* Custom Context Menu */}
			{contextMenuPosition && contextMenuRow && (
				<div
					style={{
						position: 'fixed',
						top: contextMenuPosition.y,
						left: contextMenuPosition.x,
						zIndex: 9999
					}}
				>
					<DropdownMenu
						open={true}
						onOpenChange={(open: boolean) =>
							!open && handleCloseContextMenu()
						}
					>
						<DropdownMenuTrigger asChild>
							<div style={{ width: 1, height: 1 }} />
						</DropdownMenuTrigger>
						<DropdownMenuContent
							className="min-w-[160px]"
							onEscapeKeyDown={handleCloseContextMenu}
							onInteractOutside={handleCloseContextMenu}
						>
							{onOpenInNewWindow && (
								<>
									<DropdownMenuItem
										onClick={handleContextMenuOpenInNewWindow}
									>
										<ExternalLink className="mr-2 h-4 w-4" />
										{t('buttons.openInNewWindow')}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
								</>
							)}
							{onRowDoubleClick && (
								<DropdownMenuItem
									onClick={handleContextMenuEdit}
								>
									<Pencil className="mr-2 h-4 w-4" />
									{t('buttons.edit')}
								</DropdownMenuItem>
							)}
							{onClone && (
								<DropdownMenuItem
									onClick={handleContextMenuClone}
								>
									<Copy className="mr-2 h-4 w-4" />
									{t('buttons.clone')}
								</DropdownMenuItem>
							)}
							{onDelete && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										variant="destructive"
										onClick={handleContextMenuDelete}
									>
										<Trash2 className="mr-2 h-4 w-4" />
										{t('buttons.delete')}
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			)}
		</div>
	);
}
